// React imports
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

// Generic imports
import _ from 'lodash';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

// Mui imports
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import { green, red } from '@mui/material/colors';
import { useTheme } from '@mui/material/styles';

// FontAwesomeIcon imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleCheck } from '@fortawesome/pro-light-svg-icons';

// Local imports
import { maximizeProducts, minimizeProducts, getProductInOffer } from '../utils/opportunity.js';
import PRODUCT_TYPES from '../../ProductTypes.js'
import OpportunityHeader from './OpportunityHeader.js';
import OpportunityContractBuilder from './OpportunityContractBuilder.js';
import OpportunityPreviewAndSend from './OpportunityPreviewAndSend.js';
import OpportunityOneOffAccountRouteProductsSelector from './OpportunityOneOffAccountRouteProductsSelector.js';
import TermsDialog from './OpportunityOneOffBuilder.terms.js';
import pluralize from 'pluralize';

function OpportunityContractScreen(props) {
    const { me, accounts, setAccounts, seaPorts, unlocode, suppliers, opportunity, onUpdateOpportunity, 
        onDuplicate, accesstoken, org, minimizedSearch, setMinimizedSearch, forceStep, 
        onCreateTemplate, possibleTags, exchangeRates, emailLoginInfo, onEmailRefreshConnection, onEmailLogout } = props;
    let [ dirty, setDirty ] = useState(opportunity === null);
    let [ status, setStatus ] = useState(opportunity === null ? '' : opportunity.status);
    const [ routes, setRoutes ] = useState(opportunity.routes || []);
    const [ incoterm, setIncoterm ] = useState(opportunity.incoterm || null);
    const [ buyingRates, setBuyingRates ] = useState(opportunity.buyingRates || []);
    const [ tags, setTags ] = useState(opportunity === null ? [] : opportunity.tags || []);
    const [ terms, setTerms ] = useState(opportunity?.terms);
    const [ termsOpen, setTermsOpen ] = useState(false);
    const [ errors, setErrors ] = useState({});
    const attachPDF = opportunity?.attributes?.attachPDF || false;
    const [ offers, setOffers ] = useState(opportunity === null ? [] : opportunity.offers);
    const [ transportationMethod, setTransportationMethod ] = useState(opportunity.transportationMethod || null);
    const [ activeStep, setActiveStep ] = useState(-1);
    const [ onNextLoading, setOnNextLoading ] = useState(false);
    const [ onNextDisabled, setOnNextDisabled ] = useState(false);
    const [ saveSuccess, setSaveSuccess ] = useState(false);
    const [ saving, setSaving ] = useState(false);
    const [ saveError, setSaveError ] = useState(null);
    const [ saveDisabled, setSaveDisabled ] = useState(false);
    const [ showReopen, setShowReopen ] = useState(null);
    const [ ownership, setOwnership ] = useState(null);
    const [ tagsError, setTagsError ] = useState(false);

    const theme = useTheme();

    const opportunityId = (opportunity || {}).id || null;
    const _status = (opportunity || {}).status || null;

    // Flat containers must carry dimensions, and Open Tops a height, each with a unit (the
    // product schema requires them). A contract prices the type, not a particular load, so they
    // get the same zero defaults the rate importer gives these types; without them a contract
    // using one could not be saved ("products[n].cmOrInches is a required field").
    const shapeDefaults = name => (
        name.includes('Flat') ? { length: 0, width: 0, height: 0, cmOrInches: 'cm' }
            : name.includes('Open Top') ? { height: 0, cmOrInches: 'cm' }
                : {}
    );

    const products = useMemo(() => {
        if (!transportationMethod) return [];

        let _p = _.flatten(_.compact(PRODUCT_TYPES[transportationMethod].map(p => {
            if (p.name === 'By Dimensions or Weight (sea)') return null;
            if (p.name === 'By Volume or Weight (sea)') return null;

            if (p.name === 'LCL') {
                return [{
                    id: uuidv4(),
                    type: p.name,
                    dangerous: false,
                    quantity: 1,
                    weight: 1000,
                    volume: 0,
                }, {
                    id: uuidv4(),
                    type: p.name,
                    dangerous: true,
                    quantity: 1,
                    weight: 1000,
                    volume: 0,
                }, {
                    id: uuidv4(),
                    type: p.name,
                    dangerous: false,
                    quantity: 1,
                    volume: 1,
                    weight: 0,
                }, {
                    id: uuidv4(),
                    type: p.name,
                    dangerous: true,
                    quantity: 1,
                    volume: 1,
                    weight: 0,
                }];
            }

            return [{
                id: uuidv4(), 
                type: p.name, 
                dangerous: false, 
                quantity: 1,
                ...(p.name.includes('Reefer') ? { celsiusOrFahrenheit: 'c', temperature: 0 } : {}),
                ...(p.name.includes('Air Freight') || p.name.includes('Air freight') ? { chargeableWeight: 1, weight: 1, kgOrLbs: 'kg', cmOrInches: 'cm', calculations: [{ id: 'default', method: 'chargeable-weight', chargeableWeight: 1, weight: 1, quantity: 1 }] } : {}),
                ...shapeDefaults(p.name),
            }, {
                id: uuidv4(),
                type: p.name,
                dangerous: true,
                quantity: 1,
                ...(p.name.includes('Reefer') ? { celsiusOrFahrenheit: 'c', temperature: 0 } : {}),
                ...(p.name.includes('Air Freight') || p.name.includes('Air freight') ? { chargeableWeight: 1, weight: 1, kgOrLbs: 'kg', cmOrInches: 'cm', calculations: [{ id: 'default', method: 'chargeable-weight', chargeableWeight: 1, weight: 1, quantity: 1 }] } : {}),
                ...shapeDefaults(p.name),
            }];
        })));

        return _p.map(p => {
            return opportunity?.products?.find(p2 => ((p2.type === p.type) && (p2.dangerous === p.dangerous) && (p2.volume === p.volume) && (p2.weight === p.weight))) || p;
        });
    }, [ opportunity, transportationMethod ]);

    useEffect(() => {
        setActiveStep(ac => {
            if (ac > -1) return ac;
            if (forceStep) return parseInt(forceStep);
            if (!opportunity.status) return 1;
            if (opportunity.status === 'draft') return 1;
            if (opportunity.status === 'deleted') return 0;
            return 2;
        });
        setStatus(opportunity === null ? '' : _status);
        if (opportunity) {
            setTerms(opportunity.terms);
        }
    }, [opportunityId, _status, forceStep, opportunity])

    useEffect(() => {
        const duplicated = _.some(offers, offer => {
            const fields = _.flatten(offer.sections.map(s => s.offers[0].fields.map(f => f.id)));
            return _.uniq(fields).length !== fields.length;
        });

        if (duplicated) {
            const _offers = _.cloneDeep(offers);
            _offers.forEach(offer => offer.sections.forEach(s => s.offers[0].fields.forEach(f => f.id = uuidv4())));
            setOffers(_offers);
        }
    }, [offers]);

    const account = useMemo(() => {
        if (!opportunity?.account?.id) return {};
        return accounts.find(a => a.id === opportunity?.account?.id);
    }, [opportunity, accounts]);

    function setAccount(a) { 
        if (!a.id) {
            a.id = uuidv4();
            a.status = 'draft';
            setAccounts(acc => [...acc, a]);
        }
        setDirty(true); 
        onUpdateOpportunity({ opportunity: { ...opportunity, account: a }});
    }

    function _setTransportationMethod(a) { 
        setDirty(true); 
        setTransportationMethod(a); 
    }
    function _setIncoterm(a) { setDirty(true); setIncoterm(a); }
    function _setOffers(a) { 
        setDirty(true); 
        setOffers(a);
        setErrors({ ...errors, builder: [] });
    }
    function _setRoutes(a) { setDirty(true); setRoutes(a); }
    function _setAttachPDF(a) {
        setDirty(true);
        onUpdateOpportunity({ opportunity: { ...opportunity, attributes: { ...(opportunity.attributes || {}), attachPDF: a } } });
    }
    async function _setTags({ tags }) {
        setTagsError(false);
        setTags(tags);
        try {
            const { data: _opportunity } = await axios.put(`/api/opportunities/${opportunity.id}?accesstoken=${accesstoken}&organizationId=${org.id}`, { opportunity: { tags } });
            onUpdateOpportunity({ opportunity: _opportunity });
        } catch(e) {
            setTagsError(true);
        }
    }

    async function onDelete() {
        setStatus('deleted');
        status = 'deleted';
        setDirty(true);
        dirty = true;
        setOnNextDisabled(true);
        await save();
        setOnNextDisabled(false);
    }

    async function onSave() {
        setOnNextDisabled(true);
        const res = await save();
        setOnNextDisabled(false);
        if (res) {
            setSaveSuccess(true);
            await new Promise(res => setTimeout(res, 2000));
            setSaveSuccess(false);
        }
    }

    async function onPrevStep() {
        if (activeStep === 2) {
            setActiveStep(1);
        }
    }

    async function onNextStep() {
        if (status === 'deleted') {
            setActiveStep(1);
            return;
        }

        setOnNextLoading(true);
        setSaveDisabled(true);
        const success = await save();
        setOnNextLoading(false);
        setSaveDisabled(false);

        if (success && activeStep === 1) {
            setActiveStep(2);
        }
    }

    function getErrors() {
        const er = { account: [], products: [], incoterm: [], builder: [] };

        if (activeStep === 1) {

            /* TODO
            offers.forEach((offer, oi) => {
                if (!offer.title) er.builder.push(`${oi}:offertitle`);
                offer.sections.forEach((section, si) => 
                    section.offers.forEach((sectionOffer, soi) => 
                        sectionOffer.fields.forEach((field, fi) => {
                            if (!field.type) er.builder.push(`${oi}:${si}:${soi}:${fi}:type`);
                        })
                    )
                )
            });
            */
        }
        return er;
    }


    async function save() {
        setSaveError(null);
        setErrors({ account: [], products: [], incoterm: [], builder: [] });

        const er = getErrors();

        if (!_.isEmpty(er.account) || !_.isEmpty(er.products) || !_.isEmpty(er.incoterm) || !_.isEmpty(er.builder)) {
            setErrors(er);
            return;
        }

        if (!dirty && account.status !== 'draft') return true;

        setSaving(true);

        try {
            if (account.status === 'draft') {
                const { data: newAccount } = await axios.put(`/api/accounts/${account.id}?accesstoken=${accesstoken}&organizationId=${org.id}`, {
                    account: {
                        name: account.name,
                        tags: [{ label: 'Pipeline: New' }],
                    }
                });
                setAccounts(acc => [...acc.filter(a => a.id !== account.id), newAccount ]);
                setAccount(_.pick(newAccount, ['id', 'name', 'tags']));
            }

            const { data: _opportunity } = await axios.put(`/api/opportunities/${opportunity.id}?accesstoken=${accesstoken}&organizationId=${org.id}`, {
                opportunity: {
                    ..._.omit(opportunity, ['id', 'created', 'createdBy', 'owner', 'organizationId', 'log', 'modified']),
                    status: status === 'draft' ? null : status || null,
                    type: 'contract',
                    account: _.pick(account, ['id']),
                    tags,
                    incoterm,
                    products: _.uniqBy(offers.map(offer => getProductInOffer({ offer, products })), o => o.id),
                    offers,
                    transportationMethod,
                    routes,
                    buyingRates,
                    terms
                }
            });

            onUpdateOpportunity({ opportunity: _opportunity });
            setStatus(_opportunity.status);
            setSaving(false);
            setDirty(false);
            return true;
        } catch (e) {
            setSaving(false);
            console.error(e);
            if (e.response) {
                setSaveError(e.response.data.detail || e.response.data.title || e.response.data);
            } else {
                setSaveError(e);
            }
            return false;
        }
    }

    return (
        <Box>
            <OpportunityHeader 
                org={org}
                activeStep={activeStep}
                onNextLoading={onNextLoading}
                onNextDisabled={onNextDisabled}
                status={status}
                saveSuccess={saveSuccess}
                saving={saving}
                saveError={saveError}
                setSaveError={setSaveError}
                saveDisabled={saveDisabled}
                onSave={onSave}
                onNextStep={onNextStep}
                onPrevStep={onPrevStep}
                onDelete={onDelete}
                onDuplicate={onDuplicate}
                onDuplicateDisabled={dirty}
                onReopen={() => setShowReopen(true)}
                onTakeOwnership={() => setOwnership(true)}
                account={account}
                opportunity={opportunity}
                onUpdateOpportunity={onUpdateOpportunity}
                accesstoken={accesstoken}
                owner={(opportunity || {}).owner || me.email}
                me={me}
                minimizedSearch={minimizedSearch}
                tags={tags}
                setTags={_setTags}
                possibleTags={possibleTags || []}
                tagsError={tagsError}
                setTagsError={setTagsError}
            />
                
            {activeStep === 1 && (
                <>
                    <OpportunityOneOffAccountRouteProductsSelector
                        org={org}
                        me={me}
                        account={account}
                        setAccount={setAccount}
                        routes={routes}
                        setRoutes={_setRoutes}
                        incoterm={incoterm}
                        setIncoterm={_setIncoterm}
                        seaPorts={seaPorts}
                        transportationMethod={transportationMethod}
                        setTransportationMethod={_setTransportationMethod}
                        unlocode={unlocode}
                        accesstoken={accesstoken}
                        accounts={accounts}
                        status={status}
                        disabledTransportationMethods={['air', 'land']}
                        disabledTransportationMethodsTooltip='Coming Soon'
                        opportunity={opportunity}
                        organization={org}
                        onNewOpportunity={({ opportunity: opp }) => onUpdateOpportunity({ opportunity: opp })}
                    />
                    <Box sx={{ background: 'transparent', padding: '6px 24px', mb: 3, borderBottom: '1px solid rgba(59, 130, 246, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant='body1' sx={{ fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#334155' }}>
                            Contract Builder
                        </Typography>
                    </Box>
                    <Box sx={{ padding: '24px', paddingBottom: '0px' }}>
                        <OpportunityContractBuilder
                            errors={errors.builder || []}
                            routes={routes} 
                            offers={offers} 
                            setOffers={_setOffers} 
                            buyingRates={buyingRates}
                            setBuyingRates={setBuyingRates}
                            products={products} 
                            org={org} 
                            minimizedSearch={minimizedSearch} 
                            setMinimizedSearch={setMinimizedSearch}
                            seaPorts={seaPorts}
                            unlocode={unlocode}
                            suppliers={suppliers}
                            transportationMethod={transportationMethod}
                            status={(opportunity || {}).status}
                            onCreateTemplate={onCreateTemplate}
                            owner={(opportunity || {}).owner || me.email}
                            me={me}
                            accesstoken={accesstoken}
                            exchangeRates={exchangeRates}
                            emailLoginInfo={emailLoginInfo}
                            onEmailRefreshConnection={onEmailRefreshConnection}
                            onEmailLogout={onEmailLogout}
                            attachPDF={attachPDF}
                            setAttachPDF={_setAttachPDF}
                        />
                    </Box>
                    <Box sx={{ background: 'transparent', padding: '6px 24px', mb: 3, mt: 3, borderBottom: '1px solid rgba(59, 130, 246, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant='body1' sx={{ fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#334155' }}>
                            Terms & Conditions
                        </Typography>
                    </Box>
                    <Paper sx={{ margin: '0px 24px 24px 24px', padding: '12px', border: `1px solid ${theme.palette.border1}` }} elevation={0}>
                        {!_.isEmpty(terms) && (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography variant='body2' sx={{ padding: '0px 24px' }}>
                                    The following {pluralize('term', terms.length)} and {pluralize('condition', terms.length)} will be added to the bottom of the email: {terms.map((t, i) => (
                                        <span key={t.name}>
                                            <Tooltip title={t.value}>
                                                <span style={{ textDecoration: 'underline' }}>{t.name ? _.startCase(t.name) : 'Custom terms'}</span>
                                            </Tooltip>
                                            {i < terms.length - 2 ? ',' : i === terms.length - 1 ? '' : ', and '}
                                        </span>
                                    ))}
                                </Typography>
                                <Button
                                    variant='contained'
                                    color='primary'
                                    onClick={() => setTermsOpen(true)}
                                >
                                    Edit
                                </Button>
                            </Box>
                        )}
                        {(_.isNull(terms) || _.isUndefined(terms)) && (
                            <Alert severity='info' sx={{ margin: '0px 8px' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                    <Typography variant='caption' sx={{ fontSize: '11px' }}>
                                        Using default terms & conditions{transportationMethod ? ` for ${transportationMethod}` : ''}.
                                    </Typography>
                                    <Button
                                        variant='contained'
                                        color='primary'
                                        size='small'
                                        onClick={() => setTermsOpen(true)}
                                        disabled={!transportationMethod}
                                        sx={{ textTransform: 'none', fontSize: '11px' }}
                                    >
                                        Edit
                                    </Button>
                                </Box>
                            </Alert>
                        )}
                        {_.isArray(terms) && _.isEmpty(terms) && (
                            <Alert severity='info' sx={{ margin: '0px 8px' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                    <Typography variant='caption' sx={{ fontSize: '11px' }}>
                                        No terms & conditions selected.
                                    </Typography>
                                    <Button
                                        variant='contained'
                                        color='primary'
                                        size='small'
                                        onClick={() => setTermsOpen(true)}
                                        sx={{ textTransform: 'none', fontSize: '11px' }}
                                    >
                                        Edit
                                    </Button>
                                </Box>
                            </Alert>
                        )}
                    </Paper>
                    {termsOpen && (
                        <TermsDialog
                            open={true}
                            onClose={() => setTermsOpen(false)}
                            onSave={(terms) => {
                                setTerms(terms);
                                setDirty(true);
                            }}
                            terms={terms}
                            transportationMethod={transportationMethod}
                            organization={org}
                        />
                    )}
                </>
            )}
            {activeStep === 2 && (
                <Box sx={{ padding: '24px' }}>
                    <OpportunityPreviewAndSend
                        me={me}
                        accounts={accounts}
                        accesstoken={accesstoken}
                        opportunity={opportunity}
                        organization={org}
                        onUpdateOpportunity={onUpdateOpportunity}
                        owner={(opportunity || {}).owner || me.email}
                        suppliers={suppliers}
                        tags={tags}
                        setTags={_setTags}
                        exchangeRates={exchangeRates}
                        emailLoginInfo={emailLoginInfo}
                        onEmailRefreshConnection={onEmailRefreshConnection}
                        onEmailLogout={onEmailLogout}
                        attachPDF={attachPDF}
                        setAttachPDF={_setAttachPDF}
                    />
                </Box>
            )}
            {showReopen && (
                <ReopenDialog 
                    org={org}
                    opportunity={opportunity}
                    accesstoken={accesstoken}
                    onUpdateOpportunity={onUpdateOpportunity}
                    onClose={() => setShowReopen(false)} 
                />
            )}
            {ownership && (
                <OwnershipDialog 
                    me={me}
                    org={org}
                    opportunity={opportunity}
                    accesstoken={accesstoken}
                    onUpdateOpportunity={onUpdateOpportunity}
                    account={account}
                    onClose={() => setOwnership(false)} 
                />
            )}
        </Box>
    );
}

export default OpportunityContractScreen;

function ReopenDialog(props) {
    const { onClose, org, accesstoken, onUpdateOpportunity, opportunity } = props;
    const [ creating, setCreating ] = useState(false);
    const [ error, setError ] = useState(false);
    const [ success, setSuccess ] = useState(false);

    async function onOK() {
        setCreating(true);
        setError(false);
        setSuccess(false);

        try {
            const { data: _opportunity } = await axios.put(`/api/opportunities/${opportunity.id}?accesstoken=${accesstoken}&organizationId=${org.id}`, {
                opportunity: { status: null }
            });
            onUpdateOpportunity({ opportunity: _opportunity });
            setSuccess(true);
            return;
        } catch(e) {
            setCreating(false);
            setError(true);
            return;
        }
    }

    if (success) {
        return (
            <Dialog open={true} onClose={onClose}>
                <DialogTitle>
                    Reopen Offer
                </DialogTitle>
                <DialogContent sx={{ width: '500px' }}>
                    <Box sx={{ padding: '24px 0px', color: green[500], display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <FontAwesomeIcon icon={faCircleCheck} beat size='4x' style={{ animationIterationCount: 1 }} />
                    </Box>
                    <Box sx={{ fontWeight: 800, color: green[500], display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        Offer reopened successfully.
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button variant='outlined' color='success' onClick={onClose}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        )
    }

    return (
        <Dialog open={true} onClose={onClose}>
            <DialogTitle>
                Reopen Offer
            </DialogTitle>
            <DialogContent sx={{ width: '500px' }}>
                {creating && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '24px' }}>
                        <CircularProgress />
                    </Box>
                )}
                {!creating && (
                    <Box>
                        Reopening the offer will cause the links you've sent your customers to stop working until you publish a new offer. Do you wish to continue?
                    </Box>
                )}
                {error && (
                    <Alert severity='error' sx={{ marginTop: '24px', width: '500px' }}>
                        Error reopening offer. Please try again later.
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button variant='outlined' color='secondary' onClick={onClose} disabled={creating}>
                    Cancel
                </Button>
                <Button variant='contained' color='primary' onClick={onOK} disabled={creating}>
                    OK
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function OwnershipDialog(props) {
    const { onClose, account, me, onUpdateOpportunity, accesstoken, opportunity, org } = props;
    const [ circular, setCircular ] = useState(false);
    const [ error, setError ] = useState(null);

    async function handleTransfer() {
        setError(null);
        setCircular(true);
        try {
            const { data: _opportunity } = await axios.put(`/api/opportunities/${opportunity.id}?accesstoken=${accesstoken}&organizationId=${org.id}`, {
                owner: me.email
            });

            setCircular(false);
            onUpdateOpportunity({ opportunity: _opportunity });
            onClose();

        } catch (e) {
            setCircular(false);
            console.log(e);
            setError(`Error transfering ownership: ${e.response.data.detail}`);
        }
    }

    return (
        <Dialog open={true} onClose={onClose}>
            <DialogTitle>
                Ready to Take Ownership?
            </DialogTitle>
            <DialogContent>
                <DialogContentText>
                    You're about to become the proud owner of <b>{account.name}</b>’s opportunity!
                    <br/><br/>
                    But before you take charge, please note that as the new owner, you'll be responsible for pricing, quoting, and following up with the customer regarding this inquiry.
                    <br/><br/>
                    {!circular && (
                        <Box>
                            Ready to take ownership?
                        </Box>
                    )}
                    
                    {circular && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CircularProgress />
                        </Box>
                    )}
                    {error && (
                        <Typography component={Box} variant='caption' sx={{ color: red[500], paddingTop: '24px' }} >
                            {error}
                        </Typography>
                    )}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleTransfer} autoFocus disabled={circular}>
                    Sure Do!
                </Button>
                <Button color='secondary' onClick={onClose} disabled={circular}>
                    No
                </Button>
            </DialogActions>
        </Dialog>
    )
}